import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Rectangle } from 'react-leaflet';
import { getZipCodeInfo } from '../api';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix para los iconos de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const ZipCodeMap = () => {
  const [zipCode, setZipCode] = useState('');
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const searchZipCode = async () => {
    if (!zipCode.trim()) {
      setError('Introduce un código postal');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await getZipCodeInfo(zipCode);
      setMapData(response);
      
      // Manejar diferentes estados
      if (response.hasService === false) {
        if (response.latitude === null || response.longitude === null) {
          setError('No ofrecemos servicio en este código postal y no se encontraron coordenadas');
        }
      } else if (response.hasService === true) {
        if (response.latitude === null || response.longitude === null) {
          setError('Ofrecemos servicio en este código postal pero no se encontraron coordenadas');
        }
      }
    } catch (err) {
      setError('Error al consultar el código postal');
      setMapData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchZipCode();
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>Consulta de Códigos Postales</h2>
      
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <input
          type="text"
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Introduce código postal (ej: 28001)"
          style={{
            padding: '8px 12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '14px',
            width: '200px'
          }}
        />
        <button
          onClick={searchZipCode}
          disabled={loading}
          style={{
            padding: '8px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '14px'
          }}
        >
          {loading ? 'Buscando...' : 'Buscar'}
        </button>
      </div>

      {error && (
        <div style={{ color: 'red', marginBottom: '20px', fontSize: '14px' }}>
          {error}
        </div>
      )}

      <div style={{ height: '500px', border: '1px solid #ccc', borderRadius: '4px' }}>
        <MapContainer
          center={mapData && mapData.latitude !== null && mapData.longitude !== null ? [mapData.latitude, mapData.longitude] : [40.4168, -3.7038]}
          zoom={mapData && mapData.latitude !== null && mapData.longitude !== null ? 12 : 6}
          style={{ height: '100%', width: '100%' }}
          key={mapData && mapData.latitude !== null && mapData.longitude !== null ? `${mapData.latitude}-${mapData.longitude}` : 'default'}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          
          {mapData && mapData.latitude !== null && mapData.longitude !== null && (
            <>
              <Marker position={[mapData.latitude, mapData.longitude]}>
                <Popup>
                  <div>
                    <strong>{mapData.displayName || `Código Postal: ${zipCode}`}</strong><br/>
                    <strong>Estado:</strong> {mapData.hasService ? 'Ofrecemos servicio' : 'No ofrecemos servicio'}<br/>
                    {mapData.address && (
                      <>
                        {mapData.address.city && <><strong>Ciudad:</strong> {mapData.address.city}<br/></>}
                        {mapData.address.state && <><strong>Provincia:</strong> {mapData.address.state}<br/></>}
                      </>
                    )}
                  </div>
                </Popup>
              </Marker>
              
              {mapData.bbox && mapData.bbox.length === 4 && (
                <Rectangle
                  bounds={[
                    [mapData.bbox[1], mapData.bbox[0]], // [minLat, minLng]
                    [mapData.bbox[3], mapData.bbox[2]]  // [maxLat, maxLng]
                  ]}
                  pathOptions={{ 
                    color: mapData.hasService ? 'green' : 'red', 
                    weight: 2, 
                    fillOpacity: 0.2,
                    fillColor: mapData.hasService ? 'green' : 'red'
                  }}
                />
              )}
            </>
          )}
        </MapContainer>
      </div>

      {mapData && (
        <div style={{ 
          marginTop: '20px', 
          padding: '15px', 
          backgroundColor: mapData.hasService ? '#d4edda' : '#f8d7da', 
          borderRadius: '4px',
          border: `1px solid ${mapData.hasService ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          <h4 style={{ color: mapData.hasService ? '#155724' : '#721c24' }}>
            {mapData.hasService ? '✅ Ofrecemos Servicio' : '❌ No Ofrecemos Servicio'}
          </h4>
          
          {mapData.displayName && (
            <p><strong>Ubicación:</strong> {mapData.displayName}</p>
          )}
          
          <p><strong>Código Postal:</strong> {zipCode}</p>
          
          {mapData.address && (
            <div style={{ marginBottom: '10px' }}>
              {mapData.address.city && (
                <p><strong>Ciudad:</strong> {mapData.address.city}</p>
              )}
              {mapData.address.cityDistrict && (
                <p><strong>Distrito:</strong> {mapData.address.cityDistrict}</p>
              )}
              {mapData.address.state && (
                <p><strong>Provincia:</strong> {mapData.address.state}</p>
              )}
              {mapData.address.country && (
                <p><strong>País:</strong> {mapData.address.country}</p>
              )}
            </div>
          )}
          
          <p><strong>Estado del Servicio:</strong> 
            <span style={{ 
              color: mapData.hasService ? '#155724' : '#721c24',
              fontWeight: 'bold'
            }}>
              {mapData.hasService ? ' Sí ofrecemos servicio' : ' No ofrecemos servicio'}
            </span>
          </p>

        </div>
      )}
    </div>
  );
};

export default ZipCodeMap;